<template>
    <div :class="classes">
        <CoreDropdown
            name="actAsUser"
            :condensed="true"
            :options="actAsOptions"
            :placeholder="$t('§ui.placeholder_select_master_user')"
            :modelValue="userStore.userData.owned_by"
            :validator="validator"
            :disabled="userStore.switchingOem"
            :filter="true"
            @update:modelValue="updateValue"
        >
            <template #valueTemplate="slotProps">
                <div v-if="isActAsOemActive() && !chipRemoving" @click.stop @mousedown.stop>
                    <Chip
                        class="act-as-user-chip"
                        :label="getCompanyNameById(slotProps.value)"
                        removable
                        @remove="handleChipRemove"
                    />
                </div>
                <span
                    v-else-if="chipRemoving"
                    class="chip-removing"
                    @click.stop="cancelChipRemoval"
                >
                    {{ $t('§ui.loading') }}
                </span>
                <div v-else>
                    {{ getCompanyNameById(slotProps.value) }}
                </div>
            </template>

            <template #optionTemplate="slotProps">
                <div>
                    <div>
                        {{ getCompanyName(slotProps.option.label) }}
                    </div>
                </div>
            </template>
        </CoreDropdown>

        <label
            for="actAsUser"
            :style="{
                color: !isActAsOemActive()
                    ? 'var(--color-neutral-900)'
                    : 'var(--color-primary-600)',
            }"
            >{{ $t('§ui.label_work_as') }}</label
        >
    </div>
</template>

<script>
import Chip from 'primevue/chip'
import CoreDropdown from '@/components/core/CoreDropdown.vue'
import { useOptionsStore } from '@/stores/options'
import { useUserStore } from '@/stores/user'
import { LINXFOUR_MASTER_USER_ID } from '@/data/constants'

const ROUTE_FALLBACKS = {
    ClientItem: 'ClientIndex',
    ContractShow: 'ContractIndex',
    ContractInvoices: 'ContractIndex',
    ContractFiles: 'ContractIndex',
    ContractUpdate: 'ContractIndex',
    UnitShow: 'UnitIndex',
    UnitTypeShow: 'UnitTypeIndex',
}

export default {
    name: 'ActAsUserDropdown',

    components: {
        Chip,
        CoreDropdown,
    },

    props: {
        disabled: {
            type: Boolean,
            default: false,
        },
    },

    beforeUnmount() {
        if (this.chipRemoveTimer) {
            clearTimeout(this.chipRemoveTimer)
        }
    },

    data() {
        return {
            optionsStore: useOptionsStore(),
            userStore: useUserStore(),
            chipRemoving: false,
            chipRemoveTimer: null,
        }
    },

    computed: {
        actAsOptions() {
            return (this.optionsStore.oemOptions || []).map((option) =>
                option.value === LINXFOUR_MASTER_USER_ID ? { ...option, pinned: 'first' } : option
            )
        },

        getOptions() {
            return this.optionsStore.oemOptions.map((option) => {
                return {
                    value: option.value,
                    label: option.label,
                }
            })
        },

        validator() {
            return this.isActAsOemActive()
                ? { $invalid: false, $dirty: true }
                : { $invalid: false, $dirty: false }
        },

        classes() {
            return {
                'the-act-as-user-dropdown': true,
                'the-act-as-user-dropdown--another-user': this.isActAsOemActive(),
            }
        },
    },

    methods: {
        async updateValue(id) {
            const owner_email =
                this.optionsStore.oemOptions.find((option) => option.value === id)?.label || null
            await this.userStore.switchOem({ owned_by: id, owner_email })

            if (this.$route.name in ROUTE_FALLBACKS) {
                await this.$router.push({ name: ROUTE_FALLBACKS[this.$route.name] })
            }
            window.location.reload()
        },

        getCompanyName(label) {
            if (!label || typeof label !== 'string') {
                return this.$t('§ui.message_company_missing')
            }
            return label.trim() || this.$t('§ui.message_company_missing') || ''
        },

        getCompanyNameById(obj) {
            if (obj == null || typeof obj !== 'object') {
                return this.$t('§ui.message_company_missing')
            }
            const option = this.optionsStore.oemOptions.find((option) => option.value === obj.value)
            return option ? option.label : this.$t('§ui.message_company_missing')
        },

        handleChipRemove() {
            this.chipRemoving = true
            this.chipRemoveTimer = setTimeout(() => {
                this.setToLinxfourPrimaryUser()
            }, 1500)
        },

        cancelChipRemoval() {
            if (this.chipRemoveTimer) {
                clearTimeout(this.chipRemoveTimer)
                this.chipRemoveTimer = null
            }
            this.chipRemoving = false
        },

        setToLinxfourPrimaryUser() {
            this.chipRemoving = false
            this.updateValue(LINXFOUR_MASTER_USER_ID)
        },
    },
}
</script>

<style>
.the-act-as-user-dropdown {
    position: relative;
    min-width: 300px;

    .p-select-label {
        padding: 1.5rem 2.4rem;
    }

    & > label[for='actAsUser'] {
        position: absolute;
        left: 18px;
        top: -9px;
        background-color: var(--color-white);
        font-size: 1.3rem;
        letter-spacing: 0.3px;
        padding: 1px 8px;
        border-radius: 8px;
    }

    &.the-act-as-user-dropdown--another-user {
        .p-select-label {
            padding: 0.8rem 2.4rem;
        }

        & div.p-select.p-component {
            border: 2px solid var(--color-primary-600);
        }
    }
}

.chip-removing {
    cursor: pointer;
    font-size: 1.4rem;
    text-transform: uppercase;
}

.act-as-user-chip.p-chip {
    background-color: var(--color-primary);
    color: var(--color-white);
    border-radius: 8px;

    .p-chip-remove-icon {
        font-size: 1.4rem;
        width: 2rem;
        height: 2rem;
    }
}
</style>
